import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Select } from '~/components/forms/Select/Select';

export interface CalendarSelectOption {
  value: string;
  label: string;
}

interface CalendarSelectFieldProps {
  ariaLabel: string;
  cyTag: string;
  value: string;
  onChange: (value: string) => void;
  options: CalendarSelectOption[];
  disabled?: boolean;
  className?: string;
}

type CalendarSelectFormValues = Record<string, CalendarSelectOption | null>;

/**
 * [MCV-1664] Native `<select>` popups are OS-rendered and ignore CSS, so
 * `cursor-pointer` on the control never reaches its option rows (MCV-1478
 * already fixed this for the shared `Select`, which is react-select-backed).
 * `Select` is built for react-hook-form forms (it reads `useFormContext` and
 * drives its value through `Controller`), but the calendar toolbars that need
 * a pointer cursor here -- LayerFilterBar, the provider availability scope
 * picker -- are plain controlled value/onChange components, not forms. This
 * wrapper stands up a throwaway single-field RHF form so those toolbars can
 * reuse `Select` without becoming forms themselves.
 */
export const CalendarSelectField = ({
  ariaLabel,
  cyTag,
  value,
  onChange,
  options,
  disabled,
  className,
}: CalendarSelectFieldProps) => {
  // The RHF field name doubles as the react-select input id, so it must be
  // unique per instance: LayerFilterBar renders three of these side by side.
  const fieldName = cyTag;
  const methods = useForm<CalendarSelectFormValues>({
    defaultValues: { [fieldName]: options.find((option) => option.value === value) ?? null },
  });
  const { setValue } = methods;

  // Keep the throwaway form's field in sync with the caller's controlled
  // `value` -- e.g. a "Clear All" elsewhere resetting `value` out from under
  // this component.
  useEffect(() => {
    setValue(fieldName, options.find((option) => option.value === value) ?? null);
  }, [fieldName, value, options, setValue]);

  return (
    <FormProvider {...methods}>
      <div className={className}>
        <Select
          name={fieldName}
          cyTag={cyTag}
          ariaLabel={ariaLabel}
          options={options}
          disabled={disabled}
          isSearchable={false}
          onChange={(option) => onChange((option as CalendarSelectOption | null)?.value ?? '')}
        />
      </div>
    </FormProvider>
  );
};
